import { CONFIDENCE_PERCENTILE_SLICES } from './config';
import { clamp } from './normalize';
import { computeNumberLearningRankings } from './computeRankings';
import type {
    LearningConfidenceResult,
    NumberLearningConfig,
    PercentileConfidenceSlice,
    PercentileHighlight,
} from './types';

const getConfidenceLabel = (score: number): string => {
    if (score >= 80) return 'Muito alta';
    if (score >= 60) return 'Alta';
    if (score >= 40) return 'Média';
    if (score >= 20) return 'Baixa';
    return 'Muito baixa';
};

const parseDrawNumbers = (draw: number[], maxNum: number): number[] =>
    draw
        .map(Number)
        .filter((num) => Number.isFinite(num) && num >= 1 && num <= maxNum);

const getPoolSize = (maxNum: number, percentile: number) =>
    Math.max(1, Math.ceil((maxNum * percentile) / 100));

const countHitsInPool = (
    rankedDezenas: number[],
    poolSize: number,
    actual: number[]
): number => {
    const pool = new Set(rankedDezenas.slice(0, poolSize));
    return actual.filter((dezena) => pool.has(dezena)).length;
};

const expectedRandomHitsForPool = (
    numSorteados: number,
    poolSize: number,
    maxNum: number
) => (numSorteados * poolSize) / maxNum;

const buildPercentileAnalysis = (
    percentileHits: Record<number, number[]>,
    numSorteados: number,
    maxNum: number
): PercentileConfidenceSlice[] =>
    CONFIDENCE_PERCENTILE_SLICES.map((percentile) => {
        const hits = percentileHits[percentile] ?? [];
        const poolSize = getPoolSize(maxNum, percentile);
        const avgHits = hits.length
            ? hits.reduce((acc, value) => acc + value, 0) / hits.length
            : 0;
        const maxHits = hits.length ? Math.max(...hits) : 0;
        const minHits = hits.length ? Math.min(...hits) : 0;
        const expectedRandomHits = expectedRandomHitsForPool(numSorteados, poolSize, maxNum);
        const acertividade = numSorteados > 0 ? (avgHits / numSorteados) * 100 : 0;

        return {
            percentile,
            poolSize,
            avgHits,
            maxHits,
            minHits,
            acertividade,
            expectedRandomHits,
            beatsRandom: avgHits >= expectedRandomHits,
        };
    });

const pickBestPercentile = (slices: PercentileConfidenceSlice[]): PercentileHighlight => {
    const best = slices.reduce((current, slice) => {
        if (slice.acertividade > current.acertividade) {
            return slice;
        }
        if (
            slice.acertividade === current.acertividade
            && slice.avgHits > current.avgHits
        ) {
            return slice;
        }
        return current;
    }, slices[0]);

    return {
        percentile: best.percentile,
        poolSize: best.poolSize,
        acertividade: best.acertividade,
        avgHits: best.avgHits,
        maxHits: best.maxHits,
    };
};

const pickMinPercentileGood = (
    slices: PercentileConfidenceSlice[]
): PercentileHighlight | null => {
    const goodSlices = slices.filter((slice) => slice.beatsRandom);
    if (!goodSlices.length) {
        return null;
    }

    const minSlice = goodSlices.reduce((current, slice) =>
        slice.percentile < current.percentile ? slice : current
    );

    return {
        percentile: minSlice.percentile,
        poolSize: minSlice.poolSize,
        acertividade: minSlice.acertividade,
        avgHits: minSlice.avgHits,
        maxHits: minSlice.maxHits,
    };
};

export const computeLearningConfidence = (
    historico: number[][],
    maxNum: number,
    numSorteados: number,
    config: NumberLearningConfig
): LearningConfidenceResult | null => {
    if (!historico?.length || !maxNum || !numSorteados) {
        return null;
    }

    const sliced = config.maxConcursosAnalisados && historico.length > config.maxConcursosAnalisados
        ? historico.slice(historico.length - config.maxConcursosAnalisados)
        : historico;

    const totalDraws = sliced.length;
    if (totalDraws < 2) {
        return null;
    }

    const validationDraws = Math.max(1, config.validationDraws ?? 100);
    const drawsToEvaluate = Math.min(validationDraws, totalDraws - 1);
    const startIndex = totalDraws - drawsToEvaluate;

    const details: LearningConfidenceResult['details'] = [];
    const percentileHits: Record<number, number[]> = Object.fromEntries(
        CONFIDENCE_PERCENTILE_SLICES.map((percentile) => [percentile, []])
    );

    for (let drawIndex = startIndex; drawIndex < totalDraws; drawIndex++) {
        const trainHistory = sliced.slice(0, drawIndex);
        if (!trainHistory.length) {
            continue;
        }

        const actual = parseDrawNumbers(sliced[drawIndex], maxNum);
        if (!actual.length) {
            continue;
        }

        const ranking = computeNumberLearningRankings(trainHistory, maxNum, numSorteados, config);
        if (!ranking?.rows.length) {
            continue;
        }

        const rankedDezenas = ranking.rows.map((row) => row.dezena);
        const hitsByPercentile: Record<number, number> = {};

        CONFIDENCE_PERCENTILE_SLICES.forEach((percentile) => {
            const poolSize = getPoolSize(maxNum, percentile);
            const hits = countHitsInPool(rankedDezenas, poolSize, actual);
            hitsByPercentile[percentile] = hits;
            percentileHits[percentile].push(hits);
        });

        details.push({
            drawIndex: drawIndex + 1,
            trainDraws: trainHistory.length,
            predicted: rankedDezenas.slice(0, numSorteados),
            actual,
            hits: countHitsInPool(rankedDezenas, numSorteados, actual),
            hitsByPercentile,
        });
    }

    if (!details.length) {
        return null;
    }

    const percentileSlices = buildPercentileAnalysis(percentileHits, numSorteados, maxNum);
    const bestPercentile = pickBestPercentile(percentileSlices);
    const minPercentileGood = pickMinPercentileGood(percentileSlices);
    const goodHitThreshold = minPercentileGood?.avgHits
        ?? bestPercentile.avgHits;

    const primarySlice = percentileSlices.find((slice) => slice.percentile === bestPercentile.percentile)
        ?? percentileSlices[0];

    const hitValues = details.map((item) => item.hits);
    const maxHits = primarySlice.maxHits;
    const minHits = primarySlice.minHits;
    const avgHits = primarySlice.avgHits;
    const expectedRandomHits = primarySlice.expectedRandomHits;

    const hitDistribution: Record<number, number> = {};
    for (let hits = 0; hits <= numSorteados; hits++) {
        hitDistribution[hits] = 0;
    }
    (percentileHits[bestPercentile.percentile] ?? hitValues).forEach((hits) => {
        hitDistribution[hits] = (hitDistribution[hits] ?? 0) + 1;
    });

    const avgRatio = numSorteados > 0 ? avgHits / numSorteados : 0;
    const maxRatio = numSorteados > 0 ? maxHits / numSorteados : 0;
    const vsRandomFactor = expectedRandomHits > 0
        ? clamp((avgHits / expectedRandomHits) * 50, 0, 100)
        : 50;

    const confidenceScore = clamp((avgRatio * 50 + maxRatio * 50) * 0.7 + vsRandomFactor * 0.3);

    return {
        validationDraws,
        drawsEvaluated: details.length,
        maxHits,
        avgHits,
        minHits,
        expectedRandomHits,
        confidenceScore,
        confidenceLabel: getConfidenceLabel(confidenceScore),
        hitDistribution,
        details,
        randomBaselineScore: numSorteados > 0 ? (expectedRandomHits / numSorteados) * 100 : 0,
        percentileSlices,
        bestPercentile,
        minPercentileGood,
        goodHitThreshold,
    };
};
